import { api } from './api-client';
import type {
  DayMetrics,
  Event,
  EventKeyValues,
  EventName,
  UserEvents,
  AllEventCounts,
  SearchEvents,
  ProjectEvent,
} from '@/types';

function optionalQuery(name: string, value?: string | number | null): string {
  return value != null ? `${name}=${value}` : '';
}

function toIsoDateString(
  value?: string | number | Date,
): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

export const EventsService = {
  getEventProperties: async ({
    projectId,
    eventNames = [],
  }: Event & { eventNames?: readonly string[] }) => {
    const params = new URLSearchParams();
    params.append('projectId', projectId);
    for (const name of eventNames) params.append('event', name);
    const { data } = await api.get<{ data: string[] }>(`/events/properties?${params}`);
    return data;
  },

  getEventKeyValues: async ({ projectId, key }: EventKeyValues) => {
    const params = new URLSearchParams();
    params.append('projectId', projectId);
    if (key) params.append('key', key);
    const { data } = await api.get<{ data: (string | null)[] }>(`/events/key-values?${params}`);
    return data;
  },

  getEventDetails: async ({ id }: { id: string }) => {
    const { data } = await api.get<{ data: ProjectEvent }>(`/events/${id}`);
    return data;
  },

  getEventNames: async ({ projectId }: Event) => {
    const { data } = await api.get<{ data: EventName[] }>(`/events/names?projectId=${projectId}`);
    return data;
  },

  getUserEvents: async ({ projectId, userId }: UserEvents) => {
    const { data } = await api.get<{ data: { events: ProjectEvent[] } }>(`/events?projectId=${projectId}&userId=${userId}`);
    return data.events;
  },

  getEvents: async ({ slug }: { slug: string }) => {
    const { data } = await api.get<{ data: { events: ProjectEvent[] } }>(`/events?slug=${slug}`);
    return data.events;
  },

  searchEvents: async ({
    projectId,
    page,
    cursor,
    userId = null,
    eventName,
    startDate,
    endDate,
    filterGroups,
  }: SearchEvents) => {
    const getNonEmpty = (val: string | undefined | null) => (val?.length ? val : undefined);
    const { data } = await api.post<{
      data: {
        cursor: string | null;
        events: ProjectEvent[];
        total: number;
        totalPages: number;
      };
    }>(`/events/search`, {
      event_name: getNonEmpty(eventName),
      user_id: getNonEmpty(userId),
      cursor,
      page,
      project_id: projectId,
      start_date: toIsoDateString(startDate),
      end_date: toIsoDateString(endDate),
      filter_groups: filterGroups,
    });
    return data;
  },

  getEventCounts: async ({ slug }: { slug: string | undefined }) => {
    const { data } = await api.get<{ data: number }>(`/events/count?slug=${slug}`);
    return data;
  },

  getAllEventCounts: async ({
    projectId,
    userId,
    interval,
    startDate,
    filterGroups,
    endDate,
  }: AllEventCounts) => {
    const query = [
      optionalQuery('project', projectId),
      optionalQuery('userId', userId),
      optionalQuery('startDate', startDate),
      optionalQuery('endDate', endDate),
      optionalQuery('interval', encodeURI(interval)),
    ].filter(Boolean).join('&');

    const { data } = await api.post<{ data: DayMetrics[] }>(`/events/counts?${query}`, { filter_groups: filterGroups });

    return data.reduce((acc: Record<string, DayMetrics[]>, entry: DayMetrics) => {
      const dayMetrics = { name: entry.name, date: entry.date, count: entry.count };
      const existing = acc[entry.name];
      acc[entry.name] = existing ? [...existing, dayMetrics] : [dayMetrics];
      return acc;
    }, {});
  },
};
